import { LivePayers } from "components/LivePayers";
import useFormValidation from "hooks/useFormValidation";
import { Label } from "layout/typography/Label";
import { AutoComplete } from "primereact/autocomplete";
import { useMemo, useState, useEffect } from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { useGetAllPayersQuery } from "store/queries/payers";
import { useGetAllProvidersQuery } from "store/queries/providers";
import styled from "styled-components";

import { formValidationConstants } from "../helpers/formValidations";
import { ALL_PAYERS_OPTION } from "../helpers/ruleApplicability";
import {
  ALL_PAYERS_MIXED_MESSAGE,
  mixesAllPayersWithPayers,
  RuleApplicabilityField,
} from "./RuleApplicabilityField";

const Title = styled.h3`
  margin-bottom: 16px;
`;

const StyledAutoComplete = styled(AutoComplete)`
  .p-autocomplete-multiple-container {
    width: 100%;
  }
`;

type TPayerOption = { name: string; value: number | null };

/**
 * The provider and payer section of the MPPR / Case Rate / Package create pages. CLE-2842 step 2:
 * the Payer-Provider / Typical / Fallback choice, and an "All payers" choice in the payer picker —
 * one rule per selected provider with `payerId: null`, which cannot be mixed with named payers.
 */
export function ProviderAndPayerSection() {
  const {
    control,
    trigger,
    formState: { errors },
  } = useFormContext<any>();
  const isDefault = useWatch({ control, name: "isDefault" });
  const { getFormErrorMessage, handleOnBlurField } = useFormValidation();
  const { data, isLoading } = useGetAllProvidersQuery();
  const { data: allPayers, isLoading: allPayersLoading } =
    useGetAllPayersQuery();
  const [selectedProviders, setSelectedProviders] = useState<
    { name: string; value: string }[]
  >([]);
  const [selectedPayers, setSelectedPayers] = useState<TPayerOption[]>([]);
  const [filteredPayers, setFilteredPayers] = useState<TPayerOption[]>([]);
  const [filteredProviders, setFilteredProviders] = useState<
    { name: string; value: string }[]
  >([]);

  const providers = useMemo(() => {
    if (isLoading || !data) return [];

    return data?.data.map((provider) => {
      return { name: provider.name, value: provider.id };
    });
  }, [data]);

  const payers = useMemo<TPayerOption[]>(() => {
    if (allPayersLoading || !allPayers) return [ALL_PAYERS_OPTION];
    return [
      ALL_PAYERS_OPTION,
      ...allPayers.data.map((payer: any) => {
        return { name: payer.name, value: payer.id };
      }),
    ];
  }, [allPayers]);

  useEffect(() => {
    setFilteredPayers(payers);
    setFilteredProviders(providers);
  }, [payers, providers]);

  const onPayerSearch = (e: any) => {
    const query = e.query.toLowerCase();
    const selectedPayerValues = selectedPayers.map((payer) => payer.value);
    const filtered = payers
      .filter((payer: any) => !selectedPayerValues.includes(payer.value))
      .filter((payer: any) => payer.name.toLowerCase().includes(query));
    setFilteredPayers(filtered);
  };

  const onProviderSearch = (e: any) => {
    const query = e.query.toLowerCase();
    const selectedProviderValues = selectedProviders.map(
      (provider) => provider.value
    );
    const filtered = providers
      .filter((provider) => !selectedProviderValues.includes(provider.value))
      .filter((provider) => provider.name.toLowerCase().includes(query));
    setFilteredProviders(filtered);
  };

  const onPayerSelect = (e: any, onChange: any) => {
    const selected = [...selectedPayers];
    selected.push(e.value);
    setSelectedPayers(selected);
    onChange(selected);
  };

  // CLE-2658: one or more payers added from the live-payer list. The form field holds the same
  // array the AutoComplete does, so both have to move together.
  const onPayerAdd = (added: TPayerOption[], onChange: any) => {
    const existing = new Set(selectedPayers.map((payer) => payer.value));
    const selected = [
      ...selectedPayers,
      ...added.filter((payer) => !existing.has(payer.value)),
    ];
    setSelectedPayers(selected);
    onChange(selected);
  };

  const onPayerUnselect = (e: any, onChange: any) => {
    const selected = selectedPayers.filter(
      (payer) => payer.name !== e.value.name
    );
    setSelectedPayers(selected);
    onChange(selected);
  };

  const mixesAllPayers = mixesAllPayersWithPayers(selectedPayers);

  const onProviderSelect = (e: any, onChange: any) => {
    const selected = [...selectedProviders];
    selected.push(e.value);
    setSelectedProviders(selected);
    onChange(selected);
  };

  const onProviderUnselect = (e: any, onChange: any) => {
    const selected = selectedProviders.filter(
      (provider) => provider.name !== e.value.name
    );
    setSelectedProviders(selected);
    onChange(selected);
  };

  return (
    <div className="block bg-white border-round p-4 mb-4">
      <Title>Provider and Payer Section</Title>
      <RuleApplicabilityField
        scopedFields={
          <div className="formgrid grid">
            <div className="field col-6">
              <Label htmlFor="hospitalName">Provider</Label>
              <Controller
                data-testid="provider"
                name="provider"
                control={control}
                rules={{
                  required: isDefault
                    ? false
                    : formValidationConstants.PROVIDER.PROVIDER,
                }}
                render={({ field: { onChange, onBlur } }) => (
                  <>
                    <StyledAutoComplete
                      suggestions={filteredProviders}
                      dropdown
                      multiple
                      field="name"
                      className="w-full"
                      value={selectedProviders}
                      dropdownAriaLabel="Select Provider"
                      completeMethod={onProviderSearch}
                      onSelect={(e) => onProviderSelect(e, onChange)}
                      onUnselect={(e) => onProviderUnselect(e, onChange)}
                      onBlur={() => {
                        handleOnBlurField({
                          onBlur,
                          field: "provider",
                          trigger,
                        });
                      }}
                    />
                    {getFormErrorMessage("provider", errors)}
                  </>
                )}
              />
            </div>
            <div className="field col-6">
              <Label htmlFor="payerName">Payer Name</Label>
              <Controller
                name="payerName"
                control={control}
                rules={{
                  required: isDefault
                    ? false
                    : formValidationConstants.PAYER_NAME.NAME,
                  validate: (value: unknown) =>
                    !mixesAllPayersWithPayers(value) ||
                    ALL_PAYERS_MIXED_MESSAGE,
                }}
                render={({ field: { onChange, onBlur } }) => (
                  <>
                    <StyledAutoComplete
                      suggestions={filteredPayers}
                      dropdown
                      multiple
                      field="name"
                      className="w-full"
                      value={selectedPayers}
                      dropdownAriaLabel="Select Payer"
                      completeMethod={onPayerSearch}
                      onSelect={(e) => onPayerSelect(e, onChange)}
                      onUnselect={(e) => onPayerUnselect(e, onChange)}
                      onBlur={() => {
                        handleOnBlurField({
                          onBlur,
                          field: "payerName",
                          trigger,
                        });
                      }}
                    />
                    <LivePayers
                      providerIds={selectedProviders.map(
                        (provider) => provider.value
                      )}
                      selectedPayerIds={selectedPayers
                        .map((payer) => payer.value)
                        .filter((id): id is number => id !== null)}
                      onAdd={(added) =>
                        onPayerAdd(
                          added.map((payer) => ({
                            name: payer.name,
                            value: payer.id,
                          })),
                          onChange
                        )
                      }
                    />
                    {mixesAllPayers ? (
                      <small className="p-error" role="alert">
                        {ALL_PAYERS_MIXED_MESSAGE}
                      </small>
                    ) : (
                      getFormErrorMessage("payerName", errors)
                    )}
                  </>
                )}
              />
            </div>
          </div>
        }
      />
    </div>
  );
}
